import React, { useState, useRef, useEffect } from 'react'
import { forgotPassword, generateOTP } from '../apis/services/pgApi'

// OTP Input Component (reuse from your AuthModal)
const OTPInput = ({ value, onChange }) => {
  const inputs = useRef([])

  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !e.target.value && i > 0) {
      inputs.current[i - 1]?.focus()
    }
  }

  const handleChange = (i, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1)
    const arr = value.split('')
    arr[i] = digit
    const next = arr.join('').padEnd(6, ' ').slice(0, 6)
    onChange(next.trimEnd())
    if (digit && i < 5) inputs.current[i + 1]?.focus()
  }

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (paste) {
      onChange(paste)
      inputs.current[Math.min(paste.length, 5)]?.focus()
    }
    e.preventDefault()
  }

  return (
    <div className="otp-row">
      {Array.from({ length: 6 }, (_, i) => (
        <input
          key={i}
          ref={el => inputs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className={`otp-box ${value[i] && value[i] !== ' ' ? 'filled' : ''}`}
          value={value[i] && value[i] !== ' ' ? value[i] : ''}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  )
}

const ForgotPasswordModal = ({ onClose }) => {
  const overlayRef = useRef(null)
  const [step, setStep] = useState('form') // 'form', 'otp', 'reset'
  const [contact, setContact] = useState('')
  const [contactType, setContactType] = useState('mobile') // 'mobile' or 'email'
  const [otp, setOtp] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [generatedOTP, setGeneratedOTP] = useState('')

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const handleOverlayClick = (e) => { if (e.target === overlayRef.current) onClose() }

  // Handle Send OTP
  const handleSendOTP = async () => {
    if (!contact.trim()) {
      setError('Please enter mobile number or email address')
      return
    }

    // Validate based on contact type
    if (contactType === 'mobile') {
      if (!/^\d{10}$/.test(contact.trim())) {
        setError('Please enter a valid 10-digit mobile number')
        return
      }
    } else {
      if (!/\S+@\S+\.\S+/.test(contact.trim())) {
        setError('Please enter a valid email address')
        return
      }
    }

    setLoading(true)
    setError('')

    try {
      // Prepare data for generate OTP API
      const otpData = {
        MobileNumber: contactType === 'mobile' ? contact.trim() : contact.trim() ,
        Purpose: 'FORGOT_PASSWORD',
        PGID: null,
        ReferenceID: null
      }

      console.log('Calling generate OTP with:', otpData)
      const response = await generateOTP(otpData)
      console.log('Generate OTP response:', response)

      // The OTP will be logged in console by your backend
      // For development, you can also show it in UI
      if (response && response.OTPCode) {
        setGeneratedOTP(response.OTPCode)
        console.log('OTP Generated:', response.OTPCode)
      }

      setStep('otp')
      setOtp('')
      setError('')
    } catch (err) {
      console.error('Error sending OTP:', err)
      setError(err.message || 'Failed to send OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle Reset Password
  const handleResetPassword = async () => {
    // Validate OTP
    const cleanedOTP = otp.replace(/\s/g, '')
    if (cleanedOTP.length !== 6) {
      setError('Please enter the 6-digit OTP')
      return
    }

    // Validate passwords
    if (!newPassword.trim()) {
      setError('Please enter new password')
      return
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)
    setError('')

    try {
      const resetData = {
        MobileNo: contactType === 'mobile' ? contact.trim() : null,
        EmailID: contactType === 'email' ? contact.trim() : null,
        OTP: cleanedOTP,
        NewPassword: newPassword
      }

      console.log('Calling forgot password with:', resetData)
      const response = await forgotPassword(resetData)
      console.log('Forgot password response:', response)

      if (response && response.success) {
        setStep('reset')
        setError('')
      } else {
        setError(response.message || 'Failed to reset password. Please try again.')
      }
    } catch (err) {
      console.error('Error resetting password:', err)
      setError(err.message || 'Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Resend OTP
  const handleResendOTP = async () => {
    setLoading(true)
    setError('')

    try {
      const otpData = {
        MobileNumber: contactType === 'mobile' ? contact.trim() : contact.trim() ,
        Purpose: 'FORGOT_PASSWORD',
        PGID: null,
        ReferenceID: null
      }

      const response = await generateOTP(otpData)
      console.log('OTP Resent:', response)

      if (response && response.OTPCode) {
        setGeneratedOTP(response.OTPCode)
      }

      setOtp('')
      setError('OTP resent successfully!')
      setTimeout(() => setError(''), 3000)
    } catch (err) {
      setError('Failed to resend OTP. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="auth-modal">
        <button className="auth-close" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Back button */}
        {step !== 'form' && (
          <button className="auth-back" onClick={() => { 
            setStep('form')
            setError('')
            setOtp('')
            setNewPassword('')
            setConfirmPassword('')
          }}>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M13 4L7 10L13 16" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Back
          </button>
        )}

        <div className="auth-header">
          {step === 'form' && (
            <>
              <h2>Forgot Password?</h2>
              <p>Enter your registered mobile number or email to reset password</p>
            </>
          )}
          {step === 'otp' && (
            <>
              <h2>Enter OTP</h2>
              <p>We've sent a verification code to your {contactType}</p>
            </>
          )}
          {step === 'reset' && (
            <>
              <h2>Password Reset Successfully!</h2>
              <p>You can now login with your new password</p>
            </>
          )}
        </div>

        {/* Step 1: Contact Input */}
        {step === 'form' && (
          <div className="auth-form">
            <div className="auth-type-toggle" style={{ marginBottom: '20px' }}>
              <button 
                className={contactType === 'mobile' ? 'active' : ''} 
                onClick={() => {
                  setContactType('mobile')
                  setContact('')
                  setError('')
                }}
              >
                Using Mobile Number
              </button>
              <button 
                className={contactType === 'email' ? 'active' : ''} 
                onClick={() => {
                  setContactType('email')
                  setContact('')
                  setError('')
                }}
              >
                Using Email ID
              </button>
            </div>

            <div className="auth-field">
              <label>{contactType === 'mobile' ? 'Mobile Number' : 'Email Address'}</label>
              {contactType === 'mobile' ? (
                <div className="auth-phone-row">
                  <span className="auth-phone-prefix">+91</span>
                  <input 
                    type="tel" 
                    placeholder="10-digit mobile number" 
                    value={contact} 
                    onChange={e => {
                      setContact(e.target.value.replace(/\D/g, ''))
                      setError('')
                    }} 
                    maxLength={10} 
                    autoFocus
                  />
                </div>
              ) : (
                <input 
                  type="email" 
                  placeholder="you@example.com" 
                  value={contact} 
                  onChange={e => {
                    setContact(e.target.value)
                    setError('')
                  }} 
                  autoFocus
                />
              )}
            </div>

            {error && <p className="auth-error">{error}</p>}
            
            <button 
              className="auth-submit" 
              onClick={handleSendOTP} 
              disabled={loading}
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </button>
          </div>
        )}

        {/* Step 2: OTP and New Password */}
        {step === 'otp' && (
          <div className="auth-otp-section">
            <p className="auth-otp-hint" style={{ textAlign: 'center', marginBottom: '15px' }}>
              Enter the 6-digit verification code sent to your {contactType}
            </p>
            
            <OTPInput value={otp} onChange={setOtp} />
            
            <p className="auth-otp-hint">
              Didn't receive it?{' '}
              <span className="auth-resend" onClick={handleResendOTP}>
                Resend Code
              </span>
            </p>

            <div className="auth-field" style={{ marginTop: '20px' }}>
              <label>New Password</label>
              <input 
                type="password" 
                placeholder="Enter new password (min 6 characters)" 
                value={newPassword} 
                onChange={e => {
                  setNewPassword(e.target.value)
                  setError('')
                }} 
              />
            </div>

            <div className="auth-field">
              <label>Confirm New Password</label>
              <input 
                type="password" 
                placeholder="Confirm your new password" 
                value={confirmPassword} 
                onChange={e => {
                  setConfirmPassword(e.target.value)
                  setError('')
                }} 
              />
            </div>

            {error && <p className="auth-error">{error}</p>}
            
            <button 
              className="auth-submit" 
              onClick={handleResetPassword} 
              disabled={loading}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </div>
        )}

        {/* Step 3: Success Message */}
        {step === 'reset' && (
          <div className="auth-otp-section">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#DCFCE7" />
                <path d="M20 32L28 40L44 24" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            
            <p style={{ textAlign: 'center', marginBottom: '20px', color: '#10B981' }}>
              Your password has been successfully reset!
            </p>
            
            <button className="auth-submit" onClick={onClose}>
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default ForgotPasswordModal